// client/src/components/ui/CheckoutForm.js
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import Button from './Button.js';

// Address step — shown BEFORE Elements is mounted (no stripe/elements needed)
function AddressStep({ tier, interval, onAddressComplete, creating }) {
  const [address, setAddress] = useState({
    line1: '',
    line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'US',
  });
  const [name, setName] = useState('');
  const [addressError, setAddressError] = useState(null);

  const price = interval === 'yearly'
    ? parseFloat(tier.priceyearly)
    : parseFloat(tier.pricemonthly);

  const handleSubmit = (e) => {
    e.preventDefault();
    setAddressError(null);

    // Basic validation
    if (!name.trim()) { setAddressError('Please enter your full name.'); return; }
    if (!address.line1.trim()) { setAddressError('Please enter your street address.'); return; }
    if (!address.city.trim()) { setAddressError('Please enter your city.'); return; }
    if (!address.state.trim()) { setAddressError('Please enter your state.'); return; }
    if (!address.postal_code.trim()) { setAddressError('Please enter your ZIP code.'); return; }
    if (address.country === 'US' && !/^\d{5}(-\d{4})?$/.test(address.postal_code.trim())) {
      setAddressError('Please enter a valid 5-digit ZIP code.'); return;
    }

    onAddressComplete({ name: name.trim(), address });
  };

  return (
    <form onSubmit={handleSubmit}>
      {/* Plan summary */}
      <div className="bg-royal-900/60 border border-royal-600 rounded-xl p-5 mb-6">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-display text-xl text-gold">{tier.typename}</h3>
            <p className="text-royal-200 text-base">{tier.tagline}</p>
          </div>
          <div className="text-right">
            <span className="text-2xl font-bold text-white">${price.toFixed(2)}</span>
            <span className="text-royal-200 text-base">/{interval === 'yearly' ? 'year' : 'month'}</span>
          </div>
        </div>
        <div className="border-t border-royal-700 pt-3">
          <ul className="grid grid-cols-2 gap-1.5">
            {(tier.features || []).map((f, i) => (
              <li key={i} className="text-sm text-white flex items-start gap-1.5">
                <span className="text-gold mt-0.5 shrink-0">&#10003;</span>
                <span>{f}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Billing Address */}
      <div className="mb-6">
        <label className="block text-base font-medium text-royal-100 mb-3">Billing Address</label>
        <div className="space-y-3">
          <input
            type="text"
            placeholder="Full name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full px-4 py-3 rounded-lg bg-royal-900 border border-royal-600 text-white placeholder-royal-400 focus:border-gold focus:ring-1 focus:ring-gold outline-none text-sm"
          />
          <input
            type="text"
            placeholder="Street address"
            value={address.line1}
            onChange={(e) => setAddress({ ...address, line1: e.target.value })}
            className="w-full px-4 py-3 rounded-lg bg-royal-900 border border-royal-600 text-white placeholder-royal-400 focus:border-gold focus:ring-1 focus:ring-gold outline-none text-sm"
          />
          <input
            type="text"
            placeholder="Apt, suite, etc. (optional)"
            value={address.line2}
            onChange={(e) => setAddress({ ...address, line2: e.target.value })}
            className="w-full px-4 py-3 rounded-lg bg-royal-900 border border-royal-600 text-white placeholder-royal-400 focus:border-gold focus:ring-1 focus:ring-gold outline-none text-sm"
          />
          <div className="grid grid-cols-2 gap-3">
            <input
              type="text"
              placeholder="City"
              value={address.city}
              onChange={(e) => setAddress({ ...address, city: e.target.value })}
              className="w-full px-4 py-3 rounded-lg bg-royal-900 border border-royal-600 text-white placeholder-royal-400 focus:border-gold focus:ring-1 focus:ring-gold outline-none text-sm"
            />
            <input
              type="text"
              placeholder="State"
              value={address.state}
              onChange={(e) => setAddress({ ...address, state: e.target.value })}
              className="w-full px-4 py-3 rounded-lg bg-royal-900 border border-royal-600 text-white placeholder-royal-400 focus:border-gold focus:ring-1 focus:ring-gold outline-none text-sm"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <input
              type="text"
              placeholder="ZIP code"
              value={address.postal_code}
              onChange={(e) => setAddress({ ...address, postal_code: e.target.value })}
              className="w-full px-4 py-3 rounded-lg bg-royal-900 border border-royal-600 text-white placeholder-royal-400 focus:border-gold focus:ring-1 focus:ring-gold outline-none text-sm"
            />
            <select
              value={address.country}
              onChange={(e) => setAddress({ ...address, country: e.target.value })}
              className="w-full px-4 py-3 rounded-lg bg-royal-900 border border-royal-600 text-white focus:border-gold focus:ring-1 focus:ring-gold outline-none text-sm"
            >
              <option value="US">United States</option>
              <option value="CA">Canada</option>
              <option value="GB">United Kingdom</option>
              <option value="AU">Australia</option>
              <option value="DE">Germany</option>
              <option value="FR">France</option>
            </select>
          </div>
        </div>
      </div>

      {/* Error message */}
      {addressError && (
        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
          <p className="text-red-400 text-sm">{addressError}</p>
        </div>
      )}

      {/* Continue button */}
      <Button
        type="submit"
        disabled={creating}
        className="w-full text-base py-3"
      >
        {creating ? (
          <span className="flex items-center justify-center gap-2">
            <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            Preparing checkout...
          </span>
        ) : (
          'Continue to Payment'
        )}
      </Button>

      <p className="text-center text-royal-200 text-sm mt-4 flex items-center justify-center gap-1.5">
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
        </svg>
        Secured by Stripe. Your card details never touch our servers.
      </p>
    </form>
  );
}

// Payment step — shown AFTER Elements is mounted with clientSecret
function PaymentStep({ tier, interval, taxInfo }) {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();

  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const price = interval === 'yearly'
    ? parseFloat(tier.priceyearly)
    : parseFloat(tier.pricemonthly);

  // Format cents to dollar string
  const formatCents = (cents) => `$${(cents / 100).toFixed(2)}`;

  // Determine what to show on the subscribe button
  const buttonTotal = taxInfo && taxInfo.total > 0
    ? formatCents(taxInfo.total)
    : `$${price.toFixed(2)}`;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) return;

    setProcessing(true);
    setError(null);

    const { error: submitError } = await elements.submit();
    if (submitError) {
      setError(submitError.message);
      setProcessing(false);
      return;
    }

    const { error: confirmError } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/`,
      },
      redirect: 'if_required',
    });

    if (confirmError) {
      setError(confirmError.message);
      setProcessing(false);
      return;
    }

    setSuccess(true);
    setProcessing(false);

    setTimeout(() => {
      navigate('/');
    }, 2500);
  };

  if (success) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500/20 flex items-center justify-center">
          <svg className="w-8 h-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h3 className="font-display text-2xl text-gold mb-2">Welcome to {tier.typename}!</h3>
        <p className="text-royal-100">Your subscription is active. Redirecting to home...</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit}>
      {/* Order summary with tax breakdown */}
      <div className="bg-royal-900/60 border border-royal-600 rounded-xl p-5 mb-6">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-display text-xl text-gold">{tier.typename}</h3>
            <p className="text-royal-200 text-base">{tier.tagline}</p>
          </div>
        </div>

        <div className="border-t border-royal-700 pt-3 space-y-2">
          {/* Subtotal */}
          <div className="flex justify-between text-sm">
            <span className="text-royal-200">Subtotal</span>
            <span className="text-white">
              {taxInfo ? formatCents(taxInfo.subtotal) : `$${price.toFixed(2)}`}
            </span>
          </div>

          {/* Tax */}
          <div className="flex justify-between text-sm">
            <span className="text-royal-200">Tax</span>
            <span className="text-white">
              {taxInfo && taxInfo.taxAmount > 0
                ? formatCents(taxInfo.taxAmount)
                : '$0.00'}
            </span>
          </div>

          {/* Total */}
          <div className="border-t border-royal-700 pt-2 flex justify-between">
            <span className="text-royal-100 font-medium">Total due today</span>
            <span className="text-white font-bold text-lg">
              {taxInfo && taxInfo.total > 0
                ? formatCents(taxInfo.total)
                : `$${price.toFixed(2)}`}
            </span>
          </div>

          {/* Recurring note */}
          <p className="text-royal-400 text-xs pt-1">
            Then {taxInfo && taxInfo.total > 0 ? formatCents(taxInfo.total) : `$${price.toFixed(2)}`}/{interval === 'yearly' ? 'year' : 'month'} + applicable tax
          </p>
        </div>
      </div>

      {/* Stripe Payment Element */}
      <div className="mb-6">
        <label className="block text-base font-medium text-royal-100 mb-3">Payment Details</label>
        <div className="bg-royal-900 border border-royal-600 rounded-xl p-4">
          <PaymentElement
            options={{
              layout: 'tabs',
            }}
          />
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
          <p className="text-red-400 text-sm">{error}</p>
        </div>
      )}

      {/* Submit button */}
      <Button
        type="submit"
        disabled={!stripe || processing}
        className="w-full text-base py-3"
      >
        {processing ? (
          <span className="flex items-center justify-center gap-2">
            <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            Processing...
          </span>
        ) : (
          `Subscribe — ${buttonTotal}/${interval === 'yearly' ? 'year' : 'month'}`
        )}
      </Button>

      <p className="text-center text-royal-200 text-sm mt-4 flex items-center justify-center gap-1.5">
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
        </svg>
        Secured by Stripe. Your card details never touch our servers.
      </p>
    </form>
  );
}

// Main export — renders the correct step based on prop
export default function CheckoutForm({ tier, interval, step = 'address', onAddressComplete, creating, taxInfo }) {
  if (step === 'address') {
    return <AddressStep tier={tier} interval={interval} onAddressComplete={onAddressComplete} creating={creating} />;
  }
  return <PaymentStep tier={tier} interval={interval} taxInfo={taxInfo} />;
}